<template>
  <FriedModalSubContainer :loading="!recipientPusherChannel">
    <template #header>
      <FriedModalHeader :header="header" />
    </template>
    <div v-if="error" class="error-container">
      <FriedParagraph>{{ errorDescription }}</FriedParagraph>
    </div>
    <div v-else-if="success" class="loading-container">
      <FriedSpinner />
      <FriedParagraph>{{ $t('qes_signing_document_message') }}</FriedParagraph>
    </div>
    <template v-else-if="eidEasyDoc">
      <suspense>
        <EidEasyWidget
          country-code="SE"
          language="en"
          :sandbox="sandboxMode"
          :doc-id="eidEasyDoc.eidEasyDocId"
          :client-id="clientId"
          :enabled-methods="{
            signature: signatureMethods,
            identification: false,
          }"
          :input-values="{
            idcode: '',
            phone: recipient.mobile,
            email: recipient.email,
          }"
          @success="onSuccess"
        />
        <template #fallback>
          <div class="loading-container">
            <FriedSpinner />
            <FriedParagraph>{{ $t('qes_preparing_document_message') }}</FriedParagraph>
          </div>
        </template>
      </suspense>
    </template>
    <div v-else class="loading-container">
      <FriedSpinner />
      <FriedParagraph>{{ $t('qes_preparing_document_message') }}</FriedParagraph>
    </div>
    <template #footer>
      <FriedModalButtonsFooter v-if="error">
        <FriedButton
          :button-type="ButtonType.Secondary"
          data-external="qes-modal-cancel-button"
          @click="$emit('close')"
          >{{ $t('cancel') }}</FriedButton
        >
        <FriedButton data-external="qes-modal-retry-button" @click="tryAgain">{{
          $t('qes_try_again_button')
        }}</FriedButton>
      </FriedModalButtonsFooter>
    </template>
  </FriedModalSubContainer>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { computed, ref, defineComponent, defineAsyncComponent } from 'vue';
import type { Channel } from '@getaccept/pusher';
import { ButtonType } from '@getaccept/fried-tofu';
import type { SigningPageRecipient } from '@getaccept/lib-shared/src/types/signing-page-recipient';
import { useI18n } from 'vue-i18n';
import { runningOnProduction } from '../common/utils/running-on-production';
import { useEidEasy } from './eid-easy.composable';
import { EidEasy } from './constants/eid-easy.constants';
import { EidEasyFailReason } from './types/eid-easy-fail-reason';

export default defineComponent({
  components: {
    EidEasyWidget: defineAsyncComponent({
      loader: () => import('./EidEasyWidget.vue'),
    }),
  },
  props: {
    recipientPusherChannel: {
      type: Object as PropType<Channel>,
      default: () => ({}),
    },
    recipient: {
      type: Object as PropType<SigningPageRecipient>,
      required: true,
    },
  },
  emits: ['close'],
  setup(props) {
    const { t } = useI18n();
    const { retry, eidEasyDoc, error } = useEidEasy(props.recipientPusherChannel);
    const clientId = process.env.VUE_APP_EID_EASY_CLIENT_ID;
    const success = ref(false);
    const onSuccess = () => {
      success.value = true;
    };
    const header = computed(() => {
      switch (error.value) {
        case EidEasyFailReason.DocExpired:
          return t('qes_error_header_doc_expired');
        case EidEasyFailReason.OtherActiveDoc:
          return t('qes_error_header_other_active_doc');
        case EidEasyFailReason.Unknown:
          return t('qes_error_header_unknown');
        default:
          return t('sign-document');
      }
    });
    const sandboxMode = !runningOnProduction();
    const errorDescription = computed(() => {
      switch (error.value) {
        case EidEasyFailReason.DocExpired:
          return t('qes_error_desc_doc_expired');
        case EidEasyFailReason.OtherActiveDoc:
          return t('qes_error_desc_other_active_doc');
        case EidEasyFailReason.Unknown:
          return t('qes_error_desc_unknown');
        default:
          return '';
      }
    });
    const tryAgain = () => {
      success.value = false;
      retry();
    };
    return {
      eidEasyDoc,
      clientId,
      success,
      onSuccess,
      error,
      ButtonType,
      tryAgain,
      errorDescription,
      header,
      signatureMethods: EidEasy.signatureMethods,
      sandboxMode,
    };
  },
});
</script>

<style lang="scss" scoped>
.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  height: 100%;
}
</style>
